import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { AppProvider, useApp } from "./lib/AppContext";
import { TooltipProvider } from "./components/ui/tooltip";
import Landing from "./pages/Landing";
import MobileMenu from "./pages/MobileMenu";
import Finances from "./pages/Finances";
import FinancesItems from "./pages/FinancesItems";
import FinancesAccounts from "./pages/FinancesAccounts";
import FinancesReports from "./pages/FinancesReports";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import PasswordReset, { PasswordResetConfirm } from "./pages/PasswordReset";
import InvitationAccept from "./pages/InvitationAccept";
import Home from "./pages/Home";
import Documents from "./pages/Documents";
import DocumentDetail from "./pages/DocumentDetail";
import AccountantInbox from "./pages/AccountantInbox";
import DocumentsTrash from "./pages/DocumentsTrash";
import Review from "./pages/Review";
import AuditLogs from "./pages/AuditLogs";
import Security from "./pages/Security";
import Collaborators from "./pages/Collaborators";
import SettingsHub from "./pages/Settings";
import Profile from "./pages/Profile";
import Appearance from "./pages/Appearance";
import IntegrationsGoogle from "./pages/IntegrationsGoogle";
import LifeAreas from "./pages/LifeAreas";
import Projects from "./pages/Projects";
import Tasks from "./pages/Tasks";
import TasksMatrix from "./pages/TasksMatrix";
import Storico from "./pages/Storico";
import TasksSearch from "./pages/TasksSearch";
import Mete from "./pages/Mete";
import MetaDetail from "./pages/MetaDetail";
import Azioni from "./pages/Azioni";
import AzioneDetail from "./pages/AzioneDetail";
import Agenda from "./pages/Agenda";
import Deadlines from "./pages/Deadlines";
import Trash from "./pages/Trash";
import MemoriaSegretaria from "./pages/MemoriaSegretaria";
import CronologiaSegretaria from "./pages/CronologiaSegretaria";
import SegretariaFullPage from "./pages/SegretariaFullPage";
import NotificheDigest from "./pages/NotificheDigest";
import CrmKanban from "./pages/CrmKanban";
import CrmActivities from "./pages/CrmActivities";
import LeadDetail from "./pages/LeadDetail";
import FormazioneList from "./pages/FormazioneList";
import InsuranceList from "./pages/InsuranceList";
import NotificationsPage from "./pages/NotificationsPage";
import PrivacyData from "./pages/PrivacyData";
import GdprCancelDeletion from "./pages/GdprCancelDeletion";
import DownloadApp from "./pages/DownloadApp";
import Cicli from "./pages/Cicli";
import Shell from "./components/Shell";
import ScrollRestorer from "./lib/useScrollRestoration";
import SystemStatusBanner from "./components/SystemStatusBanner";

function RequireAuth({ children, forcePasswordCheck = true }) {
  const { user, loading } = useApp();
  const loc = useLocation();
  if (loading) return <div className="min-h-dvh pxos-gradient-surface grid place-items-center opacity-70" data-testid="app-loading">Caricamento…</div>;
  if (!user) {
    // Bug 9/50 — redirect con contesto: `next` per il post-login return,
    // `reason=auth_required` per il banner esplicativo. Preserva anche
    // querystring/hash della pagina richiesta.
    const nextPath = loc.pathname + (loc.search || "") + (loc.hash || "");
    const params = new URLSearchParams({ next: nextPath, reason: "auth_required" });
    return <Navigate to={`/login?${params.toString()}`} state={{ from: nextPath }} replace />;
  }
  if (forcePasswordCheck && user.must_change_password && !loc.pathname.startsWith("/settings/security")) {
    return <Navigate to="/settings/security" replace />;
  }
  return <Shell>{children}</Shell>;
}

function RequireOwner({ children }) {
  const { user, loading } = useApp();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_owner) return <Navigate to="/" replace />;
  return children;
}

/**
 * Client-side role guard. Allows the workspace owner OR any user whose
 * `user.roles` intersects `allowed`. Renders a clean "Non autorizzato" page
 * for everyone else — no toast, no console error, no leak of which role
 * would have been required (SI-008 defense-in-depth on top of the backend
 * 403 already returned by the API).
 */
function RequireRoles({ allowed, children }) {
  const { user, loading } = useApp();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const roles = Array.isArray(user.roles) ? user.roles : [];
  const ok = user.is_owner || allowed.some((r) => roles.includes(r));
  if (!ok) return <Unauthorized />;
  return children;
}

function Unauthorized() {
  return (
    <div className="min-h-[60dvh] grid place-items-center p-8" data-testid="unauthorized-page">
      <div className="pxos-card p-8 max-w-md text-center space-y-3">
        <h1 className="font-display text-2xl font-semibold">Accesso non autorizzato</h1>
        <p className="text-sm opacity-70">
          Non hai i permessi per visualizzare questa sezione. Se pensi sia un errore,
          contatta l'amministratore del workspace.
        </p>
        <a href="/" className="pxos-btn-primary inline-block text-xs" data-testid="unauthorized-home-link">
          Torna alla home
        </a>
      </div>
    </div>
  );
}

function PWARegister() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      const onLoad = () => navigator.serviceWorker.register("/sw.js").catch(() => {});
      window.addEventListener("load", onLoad);
      return () => window.removeEventListener("load", onLoad);
    }
  }, []);
  return null;
}

const PlaceholderPolicy = ({ title }) => (
  <div className="min-h-dvh pxos-gradient-surface grid place-items-center p-8" data-testid="policy-placeholder">
    <div className="pxos-card p-8 max-w-xl text-center">
      <h1 className="font-display text-2xl font-semibold">{title}</h1>
      <p className="mt-2 opacity-70 text-sm">Documento in fase di redazione.</p>
    </div>
  </div>
);

export default function App() {
  return (
    <AppProvider>
      <TooltipProvider delayDuration={400} skipDelayDuration={200}>
        <BrowserRouter>
        <PWARegister />
        <ScrollRestorer />
        <SystemStatusBanner />
        <Toaster position="top-center" toastOptions={{
          style: { background: "var(--pxos-surface)", color: "var(--pxos-text-dark)",
                   border: "1px solid var(--pxos-border)", borderRadius: "12px" },
        }} />
        <Routes>
          <Route path="/" element={<HomeOrLanding />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/password-reset" element={<PasswordReset />} />
          <Route path="/password-reset/confirm" element={<PasswordResetConfirm />} />
          <Route path="/invitations/accept" element={<InvitationAccept />} />
          <Route path="/privacy" element={<PlaceholderPolicy title="Privacy" />} />
          <Route path="/terms" element={<PlaceholderPolicy title="Termini di servizio" />} />
          <Route path="/gdpr/cancel-deletion" element={<GdprCancelDeletion />} />
          <Route path="/download-app" element={<DownloadApp />} />

          <Route path="/documenti" element={<RequireAuth><Documents /></RequireAuth>} />
          <Route path="/documenti/cestino" element={<RequireAuth><DocumentsTrash /></RequireAuth>} />
          <Route path="/documenti/:id" element={<RequireAuth><DocumentDetail /></RequireAuth>} />
          <Route path="/documenti/:docId/revisione" element={<RequireAuth><Review /></RequireAuth>} />

          {/* Phase 2 */}
          <Route path="/agenda" element={<RequireAuth><Agenda /></RequireAuth>} />
          <Route path="/deadlines" element={<RequireAuth><Deadlines /></RequireAuth>} />
          <Route path="/segretaria" element={<RequireAuth><SegretariaFullPage /></RequireAuth>} />
          <Route path="/tasks" element={<RequireAuth><Tasks /></RequireAuth>} />
          <Route path="/tasks/history" element={<RequireAuth><Storico /></RequireAuth>} />
          <Route path="/tasks/search" element={<RequireAuth><TasksSearch /></RequireAuth>} />
          <Route path="/tasks/matrix" element={<RequireAuth><TasksMatrix /></RequireAuth>} />
          <Route path="/tasks/trash" element={<RequireAuth><Trash /></RequireAuth>} />
          <Route path="/goals" element={<Navigate to="/mete" replace />} />
          <Route path="/mete-di-vita" element={<Navigate to="/mete" replace />} />
          <Route path="/mete" element={<RequireAuth><Mete /></RequireAuth>} />
          <Route path="/mete/:id" element={<RequireAuth><MetaDetail /></RequireAuth>} />
          <Route path="/azioni" element={<RequireAuth><Azioni /></RequireAuth>} />
          <Route path="/azioni/:id" element={<RequireAuth><AzioneDetail /></RequireAuth>} />
          <Route path="/menu" element={<RequireAuth><MobileMenu /></RequireAuth>} />
          <Route path="/finances" element={<RequireAuth><RequireRoles allowed={["admin_finance_hr"]}><Finances /></RequireRoles></RequireAuth>} />
          <Route path="/finances/items" element={<RequireAuth><RequireRoles allowed={["admin_finance_hr"]}><FinancesItems /></RequireRoles></RequireAuth>} />
          <Route path="/finances/accounts" element={<RequireAuth><RequireRoles allowed={["admin_finance_hr"]}><FinancesAccounts /></RequireRoles></RequireAuth>} />
          <Route path="/finances/reports" element={<RequireAuth><RequireRoles allowed={["admin_finance_hr"]}><FinancesReports /></RequireRoles></RequireAuth>} />
          <Route path="/documents/accountant" element={<RequireAuth><RequireRoles allowed={["admin_finance_hr"]}><AccountantInbox /></RequireRoles></RequireAuth>} />

          {/* Task B · Prime Vision CRM */}
          <Route path="/prime-vision/crm" element={<RequireAuth><CrmKanban /></RequireAuth>} />
          <Route path="/prime-vision/crm/activities" element={<RequireAuth><CrmActivities /></RequireAuth>} />
          <Route path="/prime-vision/crm/:leadId" element={<RequireAuth><LeadDetail /></RequireAuth>} />

          {/* Task B · Medico */}
          <Route path="/medico/formazione" element={<RequireAuth><FormazioneList /></RequireAuth>} />
          <Route path="/medico/congressi" element={<RequireAuth><FormazioneList /></RequireAuth>} />
          <Route path="/medico/assicurazioni" element={<RequireAuth><InsuranceList /></RequireAuth>} />

          {/* Task B · Notifiche in-app */}
          <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />

          {/* Task Cicli · Fase 1 — landing con 3 card */}
          <Route path="/cicli" element={<RequireAuth><Cicli /></RequireAuth>} />
          <Route path="/cicli/aperti" element={<RequireAuth><Tasks scheduledOnly /></RequireAuth>} />
          <Route path="/cicli/da-aprire" element={<RequireAuth><Tasks inboxOnly /></RequireAuth>} />
          <Route path="/cicli/chiusi" element={<Navigate to="/tasks/history" replace />} />
          <Route path="/cicli/matrice" element={<Navigate to="/tasks/matrix" replace />} />
          <Route path="/cicli/ricerca" element={<Navigate to="/tasks/search" replace />} />
          <Route path="/cicli/cestino" element={<Navigate to="/tasks/trash" replace />} />
          <Route path="/projects" element={<RequireAuth><Projects /></RequireAuth>} />
          <Route path="/life-areas" element={<RequireAuth><LifeAreas /></RequireAuth>} />

          {/* Settings hub */}
          <Route path="/settings" element={<RequireAuth forcePasswordCheck={false}><SettingsHub /></RequireAuth>}>
            <Route path="profile" element={<Profile />} />
            <Route path="security" element={<Security />} />
            <Route path="collaborators" element={<RequireOwner><Collaborators /></RequireOwner>} />
            <Route path="audit" element={<RequireOwner><AuditLogs /></RequireOwner>} />
            <Route path="appearance" element={<Appearance />} />
            <Route path="integrations/google" element={<IntegrationsGoogle />} />
            <Route path="memoria-segretaria" element={<MemoriaSegretaria />} />
            <Route path="cronologia-segretaria" element={<CronologiaSegretaria />} />
            <Route path="notifiche" element={<NotificheDigest />} />
            <Route path="privacy-e-dati" element={<PrivacyData />} />
            <Route path="change-password" element={<Security />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </TooltipProvider>
    </AppProvider>
  );
}

function HomeOrLanding() {
  const { user, loading } = useApp();
  if (loading) return null;
  if (!user) return <Landing />;
  return <Shell><Home /></Shell>;
}
